<template>
  <ListComponent title="New Company Request" :totalCount="totalItemsData">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchCustomers"
          ></v-text-field>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(filters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>

          <Columns
            :headers="headers"
            :gridColInstance="gridColumnApi"
            localStorageKey="customerCompanyColumns"
          />
        </v-col>

        <v-col cols="4" class="px-0 d-flex justify-center align-center">
          <v-tabs height="35" v-model="tab" centered>
            <v-tabs-slider></v-tabs-slider>
            <v-tab>
              Customer
              <v-badge
                v-if="totalRequestStats.customer"
                top
                :content="totalRequestStats.customer"
                overlap
                class="mb-4 ml-3"
              ></v-badge>
            </v-tab>
            <v-tab>
              Transporter
              <v-badge
                v-if="totalRequestStats.transporter"
                top
                overlap
                :content="totalRequestStats.transporter"
                class="mb-4 ml-3"
              ></v-badge>
            </v-tab>
          </v-tabs>
        </v-col>

        <v-col
          v-if="totalItems > 0"
          cols="4"
          class="px-0 pt-0 pb-2 d-flex justify-end"
        >
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 172px)"
            class="ag-theme-alpine"
            id="customerCompanyList"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            @columnMoved="resizeGrid"
            @columnVisible="resizeGrid"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="allCompanyRequestList"
          ></AgGridVue>
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <AdminCustomerRequestFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(20)"
      />
      <CompanyRequestAttachmentDialog
        v-model="showAttachmentdialog"
        :companyRequestNo="companyRequestNo"
        :companyRequestID="companyRequestID"
        @refreshList="itemsPerPageChanged(20)"
      />

      <RejectionRemarksDialog
        v-model="showRejectionRemarksDialog"
        :companyRequestNo="companyRequestNo"
        :companyRequestID="companyRequestID"
        @refreshList="itemsPerPageChanged(20)"
      />

      <showAcceptCompanyRequestDialog
        v-model="showAcceptCompanyRequestDialog"
        :companyRequestNo="companyRequestNo"
        :companyRequestID="companyRequestID"
        @refreshList="itemsPerPageChanged(20)"
      />
    </template>

    <template #actions> </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import CompanyRequestAttachmentDialog from "@/components/ModuleBased/dialogForms/CompanyRequestAttachmentDialog.vue";
import RejectionRemarksDialog from "@/components/ModuleBased/dialogForms/RejectionRemarksDialog.vue";

import showAcceptCompanyRequestDialog from "@/components/ModuleBased/dialogForms/showAcceptCompanyRequestDialog.vue";

import CompanyRequestButton from "@/components/AgGridComponents/CompanyRequestButton.vue";

import DetailsParent from "@/components/General/DetailsParent.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import AdminCustomerRequestFilters from "@/components/ModuleBased/dialogs/filters/AdminCustomerRequestFilters.vue";
import StatusChip from "@/components/AgGridComponents/General/StatusChip.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,

    AgGridVue,
    Pagination,
    CompanyRequestAttachmentDialog,
    StatusChip,
    CompanyRequestButton,
    DetailsParent,
    agColumnHeader: CustomHeader,
    AdminCustomerRequestFilters,
    RejectionRemarksDialog,
    showAcceptCompanyRequestDialog,
  },
  data() {
    return {
      sorting: {},
      headers: [
        {
          headerName: "Company Name",
          field: "company_name",
          sortable: true,
          tooltipField: "company_name",
          minWidth: 100,
        },
        {
          headerName: "Company Type",
          field: "company_type",
          sortable: true,
          tooltipField: "company_type",
          minWidth: 100,
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 115,
          cellRenderer: "StatusChip",
          cellRendererParams: (params) => {
            return {
              status: params.value,
            };
          },
        },
        {
          headerName: "Company Email",
          field: "company_email",
          sortable: true,
          tooltipField: "company_email",
          minWidth: 100,
        },
        {
          headerName: "Service Type",
          field: "service_type",
          sortable: true,
          minWidth: 115,
        },
        {
          headerName: "Mode of Communication",
          field: "mode_of_communication",
          sortable: true,
          tooltipField: "mode_of_communication",
          minWidth: 100,
        },
        {
          headerName: "Is Existing Customer",
          field: "is_existing_customer",
          sortable: true,
          tooltipField: "is_existing_customer",
          minWidth: 100,
        },

        {
          headerName: "Contact Person",
          field: "contact_person",
          sortable: true,
          tooltipField: "contact_person",
          minWidth: 100,
        },
        {
          headerName: "Designation",
          field: "designation",
          sortable: true,
          tooltipField: "designation",
          minWidth: 100,
        },

        {
          headerName: "Address",
          field: "address_line_1",
          sortable: true,
          tooltipField: "address_line_1",
          minWidth: 100,
        },

        {
          headerName: "Contact No",
          sortable: true,
          field: "full_company_contact_number",
          tooltipField: "full_company_contact_number",
          minWidth: 150,
        },
        {
          headerName: "Rejection Remarks",
          sortable: true,
          field: "rejection_remarks",
          tooltipField: "rejection_remarks",
          minWidth: 150,
        },

        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "CompanyRequestButton",
          pinned: "right",
          minWidth: 150,
        },
      ],

      allCompanyRequestList: [],

      // pagination vars
      tab: null,
      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,
      totalItemsData: 0,
      filters: {},

      selectedCustomerId: null,
      companyRequestNo: null,
      companyRequestID: null,
      showAttachmentdialog: false,
      showRejectionRemarksDialog: false,
      showAcceptCompanyRequestDialog: false,
      permissions: null,
      userPermissions: null,

      // bulk vars
      companyPayload: [],
      totalRequestStats: [],
      detailsDrawer: false,

      // Filter Vars
      filters: {},
      showFilterDialog: false,

      uploadType: "company",
      customerComapnyBulkUploadDialog: false,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  watch: {
    tab() {
      this.getCompanyRequestList({ offset: 0, limit: this.itemsPerPage });
      this.displayDrawer = false;
    },
  },

  methods: {
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(20);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchCustomers() {
      this.pageNo = 1;
      this.getCompanyRequestList();
      this.filters = {};
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getCompanyRequestList();
    },
    prevPage() {
      this.pageNo--;
      this.getCompanyRequestList();
    },
    nextPage() {
      this.pageNo++;
      this.getCompanyRequestList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    acceptCompanyData(data) {
      this.companyRequestNo = data.company_name;
      this.companyRequestID = data?.id;
      this.showAcceptCompanyRequestDialog = true;
    },
    rejectCompanyData(data) {
      this.companyRequestNo = data.company_name;
      this.companyRequestID = data?.id;
      this.showRejectionRemarksDialog = true;
    },
    companyRequestAttachment(data) {
      this.companyRequestNo = data.company_request;
      this.companyRequestID = data?.id;
      this.showAttachmentdialog = true;
    },
    getCompanyRequestList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        company_type:
          this.tab === 0 ? "Customer" : this.tab === 1 ? "Transporter" : "",
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.user
        .getCompanyRequestLists(params)
        .then((res) => {
          this.allCompanyRequestList = res.data;
          this.totalItems = res.count;

          // delete params.limit;
          // delete params.offset;
          // delete params.status;
          // this.getTotalRequestCount(params);
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getTotalRequestCount(params) {
      this.$api.user
        .getCompanyRequestStatistics(params)
        .then((res) => {
          this.totalRequestStats = res.data;
          console.log(res.data.total_request);
          this.totalItemsData = res.data.total_request;
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't  DA!",
            color: "error",
          });
        });
    },

    // acceptCompanyData(data) {
    //   if (!Array.isArray(this.companyPayload)) {
    //     this.companyPayload = [];
    //   }

    //   this.companyPayload.push(data);

    //   this.createCompanyFromCompanyRequest();
    // },
    // createCompanyFromCompanyRequest() {
    //   this.$bus.$emit("showLoader", true);
    //   let payload = {
    //     data: this.companyPayload,
    //   };
    //   console.log("eee", payload);

    //   this.$api.user
    //     .createCompanyFormRequest(payload.data)
    //     .then((res) => {
    //       this.$bus.$emit("showLoader", false);
    //       this.$bus.$emit("showToastMessage", {
    //         message: "Company Creation Successfully",
    //         color: "success",
    //       });
    //       this.$router.push("/app/admin/company-management/customer/list");
    //     })
    //     .catch((err) => {
    //       this.$bus.$emit("showLoader", false);
    //       this.$bus.$emit("showToastMessage", {
    //         message: "Couldn't Create Company!",
    //         color: "error",
    //       });
    //       this.formErrors = err.data;
    //     });
    // },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("finanace_approval");

      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };

        this.getCompanyRequestList();
        this.getTotalRequestCount();
      }
    }
  },
};
</script>
